import Actions from 'flow/actions';
import {
    getProcessedLocalStream,
    stopStreamAndCleanup,
    stopStream,
} from 'flow/utils/video-utils';
import {
    handleVideoStreamSetupError,
    removeDuplicateMembers,
    setActiveMeetingMeetingAccessAction,
} from 'flow/actions/meeting-actions';
import {
    selectLobbyVideoStream,
    selectLobbyAudioStream,
    selectLobbyAudioActive,
    selectLobbyVideoActive,
    selectIsUserLoggedIn,
    selectLobbyVideoDeviceInUse,
} from 'flow/selectors/meeting-selectors';
import {
    selectVideoBackgroundBlurAmount,
    selectVideoBackgroundEffectMode,
    selectVideoBackgroundImageUrl,
} from 'flow/selectors/video-selectors';
import {
    MeetingLogLevel,
    addMeetingLog,
    getMeetingCode,
    getMeetingDataFromAPI,
} from 'flow/utils/meeting-utils';
import ConferencesResource from 'flow/api/conferences';
import { fromJS, List } from 'immutable';
import { networkErrorDispatcher } from 'flow/actions/app-actions';
import { endMeeting } from './meeting-actions';
import { RoomNotJoinedCause } from 'flow/utils/meeting-constants';
import { isChatSessionKey } from 'flow/utils';

const updateConferenceByKey = (callback, key, conferenceDTO) =>
    isChatSessionKey(key)
        ? ConferencesResource.updateChatConference(callback, key, conferenceDTO)
        : ConferencesResource.updateConference(callback, key, conferenceDTO);

// Bumped by every setup and stop of a lobby preview. A stream whose request is
// no longer the latest when getUserMedia resolves stops instead of landing in state.
let lobbyVideoRequest = 0;
let lobbyAudioRequest = 0;

export const setupLobbyVideoStream =
    (deviceId) => async (dispatch, getState) => {
        const lobbyVideoActive = selectLobbyVideoActive(getState());
        dispatch(
            addMeetingLog(
                `MeetingLobbyAction#setupLobbyVideoStream: lobbyVideoActive ${lobbyVideoActive}`,
            ),
        );
        if (!lobbyVideoActive) {
            return;
        }
        const request = ++lobbyVideoRequest;
        try {
            const blurAmount = selectVideoBackgroundBlurAmount(getState());
            const effectMode = selectVideoBackgroundEffectMode(getState());
            const backgroundImageUrl =
                selectVideoBackgroundImageUrl(getState());

            const videoStream = await getProcessedLocalStream(deviceId, {
                blurAmount: blurAmount,
                mode: effectMode,
                backgroundImageUrl: backgroundImageUrl,
            });

            if (
                !selectLobbyVideoActive(getState()) ||
                request !== lobbyVideoRequest
            ) {
                dispatch(
                    addMeetingLog(
                        'MeetingLobbyAction#setupLobbyVideoStream: video turned off, stopped or replaced during stream acquisition, discarding stream',
                    ),
                );
                await stopStreamAndCleanup(videoStream);
                return;
            }

            dispatch(setLobbyVideoStreamAction(videoStream));
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingLobbyAction#setupLobbyVideoStream: caught error ${e.name}: ${e.message}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            // An outdated request's error would cover the newer preview.
            if (request === lobbyVideoRequest) {
                dispatch(handleVideoStreamSetupError(e));
            }
        }
    };

export const stopLobbyVideoStream = () => async (dispatch, getState) => {
    dispatch(
        addMeetingLog(
            'MeetingLobbyAction#stopLobbyVideoStream: Stopping lobby video stream',
        ),
    );
    lobbyVideoRequest++;
    const lobbyVideoStream = selectLobbyVideoStream(getState());
    await stopStreamAndCleanup(lobbyVideoStream);
    if (selectLobbyVideoStream(getState()) === lobbyVideoStream) {
        dispatch(setLobbyVideoStreamAction(null));
    }
};

export const setupLobbyAudioStream =
    (deviceId) => async (dispatch, getState) => {
        const lobbyAudioActive = selectLobbyAudioActive(getState());
        dispatch(
            addMeetingLog(
                `MeetingLobbyAction#setupLobbyAudioStream: lobbyAudioActive ${lobbyAudioActive}`,
            ),
        );
        if (!lobbyAudioActive) {
            return;
        }
        const request = ++lobbyAudioRequest;
        try {
            const audioStream = await navigator.mediaDevices.getUserMedia({
                audio: deviceId ? { deviceId: { exact: deviceId } } : true,
            });
            if (request !== lobbyAudioRequest) {
                dispatch(
                    addMeetingLog(
                        'MeetingLobbyAction#setupLobbyAudioStream: stream stopped or replaced during acquisition, discarding stream',
                    ),
                );
                stopStream(audioStream);
                return;
            }
            dispatch(setLobbyAudioStreamAction(audioStream));
        } catch (e) {
            // Log error, unhandled for now.
            dispatch(
                addMeetingLog(
                    `MeetingLobbyAction#setupLobbyAudioStream: caught error ${e.name}: ${e.message}`,
                    MeetingLogLevel.ERROR,
                ),
            );
        }
    };

export const stopLobbyAudioStream = () => (dispatch, getState) => {
    dispatch(
        addMeetingLog(
            'MeetingLobbyAction#stopLobbyAudioStream: Stopping lobby audio stream',
        ),
    );
    lobbyAudioRequest++;
    const lobbyAudioStream = selectLobbyAudioStream(getState());
    stopStream(lobbyAudioStream);
    dispatch(setLobbyAudioStreamAction(null));
};

export const toggleLobbyAudioActive = () => async (dispatch, getState) => {
    const audioActive = selectLobbyAudioActive(getState());
    dispatch(
        addMeetingLog(
            `MeetingLobbyAction#toggleLobbyAudioActive: audioActive ${audioActive}`,
        ),
    );
    dispatch(setLobbyAudioActiveAction(!audioActive));

    if (audioActive) {
        dispatch(stopLobbyAudioStream());
    } else {
        await dispatch(setupLobbyAudioStream());
    }
};

export const toggleLobbyVideoActive = () => async (dispatch, getState) => {
    const state = getState();
    const videoActive = selectLobbyVideoActive(state);
    dispatch(
        addMeetingLog(
            `MeetingLobbyAction#toggleLobbyVideoActive: videoActive ${videoActive}`,
        ),
    );
    dispatch(setLobbyVideoActiveAction(!videoActive));

    if (videoActive) {
        dispatch(stopLobbyVideoStream());
    } else {
        const videoDevice = selectLobbyVideoDeviceInUse(state);
        const videoDeviceId = videoDevice && videoDevice.deviceId;
        await dispatch(setupLobbyVideoStream(videoDeviceId));
    }
};

/**
 * Fetches lobby data into the store.
 *
 * @param {string} entityId
 * @param {boolean} [clearOnFailure]
 * @param {?string} [prefetchedMeetingCode] Pass an already-resolved meeting
 *   code to skip the internal getMeetingCode lookup — useful when the caller
 *   has just resolved it for the same entity (avoids a duplicate round-trip).
 */
export const fetchLobbyData =
    (entityId, clearOnFailure = false, prefetchedMeetingCode = null) =>
    async (dispatch, getState) => {
        const isLoggedIn = selectIsUserLoggedIn(getState());
        try {
            const meetingCode =
                prefetchedMeetingCode ??
                (await dispatch(getMeetingCode(entityId)));
            const lobbyData = await getMeetingDataFromAPI(
                meetingCode,
                isLoggedIn,
            );
            dispatch(setLobbyMeetingCodeAction(lobbyData.get('meetingCode')));
            // A personal conference (not a room) has no chatSessionId in the
            // API response at all, so `.get` returns undefined here — but
            // selectViewerCanRegenerateLink treats undefined as "not loaded
            // yet" and fails closed. Now that the fetch has resolved, that
            // undefined is a known, genuine absence, so normalize it to null
            // or the regenerate-link button never appears for personal rooms.
            dispatch(
                setLobbyChatSessionIdAction(
                    lobbyData.get('chatSessionId') ?? null,
                ),
            );
            dispatch(setLobbyPinAction(lobbyData.get('pin')));
            dispatch(setLobbyMember(lobbyData.get('conferenceMembers')));
            dispatch(setLobbyNumberAction(lobbyData.get('number')));
            dispatch(
                setLobbyMeetingAccessAction(lobbyData.get('meetingAccess')),
            );
            dispatch(
                setLobbyRequireHostPresentAction(
                    lobbyData.get('requireHostPresent'),
                ),
            );
            dispatch(setLobbyHostPresentAction(lobbyData.get('hostPresent')));
            dispatch(
                setLobbyMeetingJoinBlockedReasonAction(
                    lobbyData.get('meetingJoinBlockedReason'),
                ),
            );
            dispatch(
                setLobbyViewerIsMeetingHostAction(
                    lobbyData.get('viewerIsMeetingHost'),
                ),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingLobbyAction#getLobbyData: caught error: ${JSON.stringify(
                        e,
                    )}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            if (clearOnFailure) {
                dispatch(setLobbyMeetingCodeAction(null));
                dispatch(setLobbyChatSessionIdAction(null));
                dispatch(setLobbyPinAction(null));
                dispatch(setLobbyMember(null));
                dispatch(setLobbyNumberAction(null));
                dispatch(setLobbyMeetingAccessAction(null));
                dispatch(setLobbyRequireHostPresentAction(null));
                dispatch(setLobbyHostPresentAction(null));
                dispatch(setLobbyMeetingJoinBlockedReasonAction(null));
                dispatch(setLobbyViewerIsMeetingHostAction(null));
            }

            return;
        }
    };

export const updateMeetingAccess = (key, meetingAccess) => (dispatch) => {
    const conferenceDTO = fromJS({ meetingAccess });
    return new Promise((resolve, reject) => {
        updateConferenceByKey(
            {
                onResult(newConferenceDTO) {
                    const access =
                        newConferenceDTO && newConferenceDTO.meetingAccess;
                    // Only overwrite the store if the response actually echoed
                    // the field back — an absent field must not snap the UI
                    // back to a stale/default value after a real change.
                    if (access !== undefined) {
                        dispatch(setLobbyMeetingAccessAction(access));
                        // The host controls panel reads the activeMeeting copy
                        // (see MeetingHostControlsPanel), which otherwise only
                        // gets refreshed on the next 10s poll tick.
                        dispatch(setActiveMeetingMeetingAccessAction(access));
                    }
                    resolve(access);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch)(error);
                    reject(error);
                },
            },
            key,
            conferenceDTO,
        );
    });
};

export const updateRequireHostPresent =
    (key, requireHostPresent) => (dispatch) => {
        const conferenceDTO = fromJS({ requireHostPresent });
        return new Promise((resolve, reject) => {
            updateConferenceByKey(
                {
                    onResult(newConferenceDTO) {
                        const value =
                            newConferenceDTO &&
                            newConferenceDTO.requireHostPresent;
                        if (value !== undefined) {
                            dispatch(setLobbyRequireHostPresentAction(value));
                        }
                        resolve(value);
                    },
                    onError: (error) => {
                        networkErrorDispatcher(dispatch)(error);
                        reject(error);
                    },
                },
                key,
                conferenceDTO,
            );
        });
    };

export const handleRoomNotJoined = (roomName, cause) => async (dispatch) => {
    if (cause === RoomNotJoinedCause.ROOM_FULL.key) {
        dispatch(
            addMeetingLog(
                'MeetingLobbyActions#handleRoomNotJoined: Room was full, hangup and set warning for 30s',
            ),
        );
        await dispatch(endMeeting());
        dispatch(
            setLobbyRoomNotJoinedAction(
                roomName,
                'videoconference.error.room_full',
            ),
        );
        setTimeout(() => {
            dispatch(setLobbyRoomNotJoinedAction(null, null));
        }, 30000);
    } else {
        dispatch(
            addMeetingLog(
                `MeetingLobbyActions#handleRoomNotJoined: Room was not full, the cause was ${cause}`,
                MeetingLogLevel.WARNING,
            ),
        );
    }
};

export const setLobbyMember = (members) => (dispatch) => {
    if (members) {
        const memberList = removeDuplicateMembers(members);
        dispatch(setLobbyMemberAction(memberList));
    } else {
        dispatch(
            addMeetingLog(
                'MeetingLobbyActions#setLobbyMember: No members to set to remove duplicates',
                MeetingLogLevel.WARNING,
            ),
        );
        dispatch(setLobbyMemberAction(new List()));
    }
};

const setLobbyVideoStreamAction = (videoStream) => ({
    type: Actions.MEETING_SET_LOBBY_VIDEO_STREAM,
    videoStream,
});

const setLobbyAudioStreamAction = (audioStream) => ({
    type: Actions.MEETING_SET_LOBBY_AUDIO_STREAM,
    audioStream,
});

export const setLobbyAudioActiveAction = (audioActive) => ({
    type: Actions.MEETING_SET_LOBBY_AUDIO_ACTIVE,
    audioActive,
});

export const setLobbyVideoActiveAction = (videoActive) => ({
    type: Actions.MEETING_SET_LOBBY_VIDEO_ACTIVE,
    videoActive,
});

export const setLobbyMeetingCodeAction = (meetingCode) => ({
    type: Actions.MEETING_SET_LOBBY_MEETING_CODE,
    meetingCode,
});

export const setLobbyChatSessionIdAction = (chatSessionId) => ({
    type: Actions.MEETING_SET_LOBBY_CHAT_SESSION_ID,
    chatSessionId,
});

export const setLobbyPinAction = (pin) => ({
    type: Actions.MEETING_SET_LOBBY_PIN,
    pin,
});

const setLobbyNumberAction = (number) => ({
    type: Actions.MEETING_SET_LOBBY_NUMBER,
    number,
});

const setLobbyMemberAction = (members) => ({
    type: Actions.MEETING_ADD_LOBBY_MEMBERS,
    members,
});

export const setLobbyMeetingAccessAction = (meetingAccess) => ({
    type: Actions.MEETING_SET_LOBBY_MEETING_ACCESS,
    meetingAccess,
});

export const setLobbyRequireHostPresentAction = (requireHostPresent) => ({
    type: Actions.MEETING_SET_LOBBY_REQUIRE_HOST_PRESENT,
    requireHostPresent,
});

// Home's runtime answer to requireHostPresent (VID-198): whether a host has
// started this session. Only meaningful on a live fetch; see
// selectLobbyHostGateClosed for how a missing value is read.
export const setLobbyHostPresentAction = (hostPresent) => ({
    type: Actions.MEETING_SET_LOBBY_HOST_PRESENT,
    hostPresent,
});

export const setLobbyMeetingJoinBlockedReasonAction = (
    meetingJoinBlockedReason,
) => ({
    type: Actions.MEETING_SET_LOBBY_MEETING_JOIN_BLOCKED_REASON,
    meetingJoinBlockedReason,
});

export const setLobbyViewerIsMeetingHostAction = (viewerIsMeetingHost) => ({
    type: Actions.MEETING_SET_LOBBY_VIEWER_IS_MEETING_HOST,
    viewerIsMeetingHost,
});

export const setLobbyMicDeviceInUseAction = (deviceInUse) => ({
    type: Actions.MEETING_SET_LOBBY_MIC_DEVICE_IN_USE,
    deviceInUse,
});

export const setLobbyVideoDeviceInUseAction = (deviceInUse) => ({
    type: Actions.MEETING_SET_LOBBY_VIDEO_DEVICE_IN_USE,
    deviceInUse,
});

export const setLobbyAudioDeviceInUseAction = (deviceInUse) => ({
    type: Actions.MEETING_SET_LOBBY_AUDIO_DEVICE_IN_USE,
    deviceInUse,
});

export const setLobbyCameraErrorAction = (cameraError) => ({
    type: Actions.MEETING_SET_LOBBY_CAMERA_ERROR,
    cameraError,
});

export const setLobbyRoomNotJoinedAction = (roomName, cause) => ({
    type: Actions.MEETING_SET_LOBBY_ROOM_NOT_JOINED,
    roomName,
    cause,
});
